import {useEffect, useRef, useCallback, useState} from 'react';
import ParticleBackground from '../components/ParticleBackground';
import {WavRecorder, WavStreamPlayer} from '../lib/wavtools/index.js';
import {WavRenderer} from '../utils/wav_renderer';
import {X, Zap, Mic, MessageSquare, ChevronDown, Calendar} from 'react-feather';
import {Button} from '../components/ui/button';
import {Input} from "../components/ui/input"
import {Label} from "../components/ui/label"
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../components/ui/dialog"
import './PatientHistoryPage.scss';
import {chatService} from '../services/api/chatService';
import {environment} from '../config/environment';
import {storage} from '../utils/storage';
import {startSessionTrace, logMessage, endSessionTrace, logError, flushLangfuse} from '../utils/langfuse';
import {Card, CardContent, CardHeader, CardTitle, CardFooter} from "../components/ui/card";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/CustomDatePicker.scss";

// Add type declaration for webkitAudioContext
declare global {
    interface Window {
        webkitAudioContext: typeof AudioContext;
    }
}

interface WebRTCMessage {
    type: string;
    session?: any;
    item?: any;
    response?: any;
    trackId?: string;
    offset?: number;
    error?: string;
    transcript?: string;
}

interface AudioState {
    stream: MediaStream | null;
    audioContext: AudioContext | null;
    audioTrack: MediaStreamTrack | null;
}

interface RealtimeEvent {
    time: string;
    source: 'client' | 'server';
    count?: number;
    event: { [key: string]: any };
}

interface PatientHistoryPageProps {
    onLogout: () => Promise<void>;
}

interface FormattedContent {
    transcript?: string;
    text?: string;
    tool?: any;
    output?: string;
    audio?: Uint8Array;
}

interface ConversationItem {
    id: string;
    type: string;
    role?: string;
    formatted: FormattedContent;
    object: string;
}


export function PatientHistoryPage({onLogout}: PatientHistoryPageProps) {
    /**
     * Ask user for API Key
     * If we're using the local relay server, we don't need this
     */
    

    /**
     * Instantiate:
     * - WavRecorder (speech input)
     * - WavStreamPlayer (speech output)
     */
    const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
    const dataChannelRef = useRef<RTCDataChannel | null>(null);
    const audioStateRef = useRef<AudioState>({
        stream: null,
        audioContext: null,
        audioTrack: null
    });
    const audioElementRef = useRef<HTMLAudioElement | null>(null);

    const wavRecorderRef = useRef<WavRecorder>(
        new WavRecorder({sampleRate: 24000})
    );
    const wavStreamPlayerRef = useRef<WavStreamPlayer>(
        new WavStreamPlayer({sampleRate: 24000})
    );

    /**
     * References for
     * - Rendering audio visualization (canvas)
     * - Autoscrolling event logs
     * - Timing delta for event log displays
     */
    const clientCanvasRef = useRef<HTMLCanvasElement>(null);
    const serverCanvasRef = useRef<HTMLCanvasElement>(null);
    const eventsScrollHeightRef = useRef(0);
    const eventsScrollRef = useRef<HTMLDivElement>(null);
    const startTimeRef = useRef<string>(new Date().toISOString());

    /**
     * All of our variables for displaying application state
     * - items are all conversation items (dialog)
     * - realtimeEvents are event logs, which can be expanded
     * - memoryKv is for set_memory() function
     * - coords, marker are for get_weather() function
     */
    const [items, setItems] = useState<ConversationItem[]>([]);
    const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isEndingSession, setIsEndingSession] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [textMessage, setTextMessage] = useState('');
    const [patientInfo, setPatientInfo] = useState({
        name: '',
        dateOfBirth: '',
        gender: ''
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const datePickerRef = useRef<any>(null);
    const [formErrors, setFormErrors] = useState({
        name: '',
        dateOfBirth: '',
        gender: '',
        communicationMode: ''
    });
    const [isConnecting, setIsConnecting] = useState(false);
    const [communicationMode, setCommunicationMode] = useState<'audio' | 'text' | ''>('');
    const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
    const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(false);
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(10); // 10 seconds countdown
    
    // Timeout refs
    const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<Date>(new Date());

    // Session timeout configuration (in milliseconds)
    const SESSION_TIMEOUT = 70 * 1000; // 70 seconds total (60s inactivity + 10s warning)
    const WARNING_BEFORE_TIMEOUT = 10 * 1000; // Show warning 10 seconds before timeout
    
    /**
     * Reset activity timer on user interaction
     */
    const resetActivityTimer = useCallback(() => {
        if (!isConnected) return;
        
        lastActivityRef.current = new Date();
        
        // Clear existing timeouts
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        
        // Hide warning if it's showing
        if (showTimeoutWarning) {
            setShowTimeoutWarning(false);
            setTimeoutCountdown(10);
        }
        
        // Set warning timeout (60 seconds into the 70 second timeout)
        warningTimeoutRef.current = setTimeout(() => {
            setShowTimeoutWarning(true);
            setTimeoutCountdown(10);
            
            // Start countdown
            let countdown = 10;
            countdownIntervalRef.current = setInterval(() => {
                countdown -= 1;
                setTimeoutCountdown(countdown);
                
                if (countdown <= 0) {
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                    }
                }
            }, 1000);
        }, SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT);
        
        // Set final timeout (70 seconds)
        inactivityTimeoutRef.current = setTimeout(() => {
            console.log('Session timeout - automatically ending session after 70 seconds of inactivity');
            setShowTimeoutWarning(false); // Close the warning dialog immediately
            disconnectConversation(true); // Auto-save the session
        }, SESSION_TIMEOUT);
    }, [isConnected, showTimeoutWarning]);

    /**
     * Calculate age from date of birth
     */
    const calculateAge = (dateOfBirth: string): number => {
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        
        return age;
    };

    /**
     * Utility for formatting the timing of logs
     */
    const formatTime = useCallback((timestamp: string) => {
        const startTime = startTimeRef.current;
        const t0 = new Date(startTime).valueOf();
        const t1 = new Date(timestamp).valueOf();
        const delta = t1 - t0;
        const hs = Math.floor(delta / 10) % 100;
        const s = Math.floor(delta / 1000) % 60;
        const m = Math.floor(delta / 60_000) % 60;
        const pad = (n: number) => {
            let s = n + '';
            while (s.length < 2) {
                s = '0' + s;
            }
            return s;
        };
        return `${pad(m)}:${pad(s)}.${pad(hs)}`;
    }, []);

    /**
     * Show disclaimer dialog before connecting
     */
    const connectConversation = useCallback(async () => {
        if (!validateForm()) {
            return;
        }
        
        // Show disclaimer dialog first
        setShowDisclaimerDialog(true);
    }, [patientInfo, communicationMode]);

    /**
     * Connect to conversation using WebRTC (called after disclaimer consent)
     */
    const actuallyConnectConversation = useCallback(async () => {
        setIsConnecting(true);
        setShowDisclaimerDialog(false);

        try {
            // Get auth data for Langfuse tracing
            const authData = storage.getAuthData();
            const sessionId = authData?.session?.id || 0;
            const patientId = authData?.user?.id || 0;
            const specialty = authData?.session?.specialty;

            // Start Langfuse session trace
            startSessionTrace({
                sessionId,
                patientId,
                patientName: patientInfo.name,
                patientAge: calculateAge(patientInfo.dateOfBirth),
                patientGender: patientInfo.gender,
                communicationMode: communicationMode as 'audio' | 'text',
                specialty,
            });

            // First send patient info to backend
            await chatService.updatePatientInfo({
                name: patientInfo.name,
                age: calculateAge(patientInfo.dateOfBirth),
                dateOfBirth: patientInfo.dateOfBirth,
                gender: patientInfo.gender
            });

            // Then proceed with WebRTC setup
            await setupWebRTC();
            setIsConnected(true);
            setRealtimeEvents([]);

            // Initialize audio only if in audio mode
            if (communicationMode === 'audio') {
                const wavRecorder = wavRecorderRef.current;
                const wavStreamPlayer = wavStreamPlayerRef.current;

                await wavStreamPlayer.connect();
                const audioContext = wavStreamPlayer.context;

                if (audioContext) {
                    console.log('AudioContext state:', audioContext.state);
                    if (audioContext.state === 'suspended') {
                        await audioContext.resume();
                    }
                }
            }
        } catch (error) {
            console.error('Error during conversation connection:', error);
            // Log error to Langfuse
            logError('connection_error', error instanceof Error ? error.message : 'Unknown error');
            await disconnectConversation();
        } 
    }, [patientInfo, communicationMode]);

    /**
     * Disconnect and cleanup WebRTC resources
     */
    const disconnectConversation = useCallback(async (shouldSaveHistory: boolean = true) => {
        try {
            if (shouldSaveHistory) {
                setIsEndingSession(true);
                setShowThankYou(true);
            }

            // Clean up audio element
            if (audioElementRef.current) {
                audioElementRef.current.srcObject = null;
                audioElementRef.current = null;
            }

            // Send chat history only if requested
            if (shouldSaveHistory) {
                const completedMessages = items
                    .filter(item =>
                        item.type === 'message' &&
                        item.formatted?.transcript &&
                        item.role
                    )
                    .map(item => ({
                        role: item.role,
                        transcript: item.formatted.transcript
                    }));

                if (completedMessages.length > 0) {
                    try {
                        await chatService.sendChatHistory(completedMessages);
                        // End Langfuse session trace with success
                        endSessionTrace(completedMessages.length, 'success');
                    } catch (error) {
                        console.error('Error sending chat history:', error);
                        // End Langfuse session trace with failure
                        endSessionTrace(completedMessages.length, 'failed');
                    }
                } else {
                    // End session even if no messages
                    endSessionTrace(0, 'success');
                }
            }

            // Cleanup WebRTC
            if (dataChannelRef.current) {
                dataChannelRef.current.close();
                dataChannelRef.current = null;
            }

            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }

            // Cleanup audio
            const {stream, audioTrack, audioContext} = audioStateRef.current;
            if (audioTrack) {
                audioTrack.stop();
            }
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            if (audioContext) {
                await audioContext.close();
            }

            // Reset state
            audioStateRef.current = {
                stream: null,
                audioContext: null,
                audioTrack: null
            };

            // Clean up timeout timers
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
                inactivityTimeoutRef.current = null;
            }
            if (warningTimeoutRef.current) {
                clearTimeout(warningTimeoutRef.current);
                warningTimeoutRef.current = null;
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
            
            // Reset UI state
            setIsConnected(false);
            setRealtimeEvents([]);
            setItems([]);
            setShowTimeoutWarning(false);
            setTimeoutCountdown(10);

            // Trigger logout after delay only if saving history
            if (shouldSaveHistory) {
                setTimeout(() => {
                    onLogout();
                }, 2000);
            }

        } catch (error) {
            console.error('Error during conversation disconnect:', error);
        }
    }, [onLogout, items]);

    /**
     * Flush Langfuse on component unmount
     */
    useEffect(() => {
        return () => {
            flushLangfuse();
        };
    }, []);

    /**
     * Auto-scroll the event logs
     */
    useEffect(() => {
        if (eventsScrollRef.current) {
            const eventsEl = eventsScrollRef.current;
            const scrollHeight = eventsEl.scrollHeight;
            // Only scroll if height has just changed
            if (scrollHeight !== eventsScrollHeightRef.current) {
                eventsEl.scrollTop = scrollHeight;
                eventsScrollHeightRef.current = scrollHeight;
            }
        }
    }, [realtimeEvents]);

    /**
     * Auto-populate patient information from stored auth data
     */
    useEffect(() => {
        const authData = storage.getAuthData();
        if (authData?.user) {
            const userData = authData.user;
            
            // Determine the preferred name
            let preferredName = '';
            if (userData.preferred_name) {
                // Use preferred_name if it exists
                preferredName = userData.preferred_name;
            } else if (userData.name) {
                // Extract first name from full name if preferred_name is null
                // Remove common titles (Mr, Mrs, Miss, Ms, Dr, Prof, etc.)
                const titles = /^(Mr\.?|Mrs\.?|Miss|Ms\.?|Dr\.?|Prof\.?|Professor|Sir|Lady|Lord)\s+/i;
                const nameWithoutTitle = userData.name.trim().replace(titles, '');
                
                // Get the first word from the name (after removing title)
                preferredName = nameWithoutTitle.split(' ')[0];
            }
            
            setPatientInfo(prev => ({
                name: preferredName || prev.name,
                dateOfBirth: userData.date_of_birth || prev.dateOfBirth,
                gender: userData.gender || prev.gender
            }));
        }
    }, []); // Run only once on component mount

    /**
     * Auto-scroll the conversation logs
     */
    useEffect(() => {
        const conversationEls = [].slice.call(
            document.body.querySelectorAll('[data-conversation-content]')
        );
        for (const el of conversationEls) {
            const conversationEl = el as HTMLDivElement;
            conversationEl.scrollTop = conversationEl.scrollHeight;
        }
    }, [items]);

    /**
     * Set up render loops for the visualization canvas
     */
    useEffect(() => {
        let isLoaded = true;

        const wavRecorder = wavRecorderRef.current;
        const clientCanvas = clientCanvasRef.current;
        let clientCtx: CanvasRenderingContext2D | null = null;

        const wavStreamPlayer = wavStreamPlayerRef.current;
        const serverCanvas = serverCanvasRef.current;
        let serverCtx: CanvasRenderingContext2D | null = null;

        const render = () => {
            if (isLoaded) {
                if (clientCanvas) {
                    if (!clientCanvas.width || !clientCanvas.height) {
                        clientCanvas.width = clientCanvas.offsetWidth;
                        clientCanvas.height = clientCanvas.offsetHeight;
                    }
                    clientCtx = clientCtx || clientCanvas.getContext('2d');
                    if (clientCtx) {
                        clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
                        const result = wavRecorder.recording
                            ? wavRecorder.getFrequencies('voice')
                            : {values: new Float32Array([0])};
                        WavRenderer.drawBars(
                            clientCanvas,
                            clientCtx,
                            result.values,
                            '#0099ff',
                            10,
                            0,
                            8
                        );
                    }
                }
                if (serverCanvas) {
                    if (!serverCanvas.width || !serverCanvas.height) {
                        serverCanvas.width = serverCanvas.offsetWidth;
                        serverCanvas.height = serverCanvas.offsetHeight;
                    }
                    serverCtx = serverCtx || serverCanvas.getContext('2d');
                    if (serverCtx) {
                        serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
                        const result = wavStreamPlayer.analyser
                            ? wavStreamPlayer.getFrequencies('voice')
                            : {values: new Float32Array([0])};
                        WavRenderer.drawBars(
                            serverCanvas,
                            serverCtx,
                            result.values,
                            '#009900',
                            10,
                            0,
                            8
                        );
                    }
                }
                window.requestAnimationFrame(render);
            }
        };
        render();

        return () => {
            isLoaded = false;
        };
    }, []);

    // Event handler definitions moved outside useEffect
    const handleRealtimeEvent = useCallback((realtimeEvent: WebRTCMessage) => {
        setRealtimeEvents((prevEvents) => {
            const lastEvent = prevEvents[prevEvents.length - 1];
            if (lastEvent?.event.type === realtimeEvent.type) {
                return [
                    ...prevEvents.slice(0, -1),
                    {...lastEvent, count: (lastEvent.count || 0) + 1}
                ];
            }
            return [...prevEvents, {
                time: new Date().toISOString(),
                source: 'server',
                event: realtimeEvent
            }];
        });
    }, []);

    const handleError = useCallback((error: Error) => {
        console.error('WebRTC error:', error);
        // If connection error, try to reconnect
        if (error.message === 'Connection lost') {
            setupWebRTC().catch(console.error);
        }
    }, []);

    const handleInterruption = useCallback(async () => {
        const trackSampleOffset = await wavStreamPlayerRef.current.interrupt();
        if (trackSampleOffset?.trackId) {
            sendMessage({
                type: 'interrupt',
                trackId: trackSampleOffset.trackId,
                offset: trackSampleOffset.offset
            });
        }
    }, []);

    const handleConversationUpdate = useCallback(async ({item, delta}: any) => {
        if (peerConnectionRef.current?.connectionState !== 'connected') {
            console.warn('WebRTC not connected during conversation update');
            return;
        }

        try {
            // Handle streaming audio from the assistant
            if (delta?.audio) {
                try {
                    // Convert audio data to Int16Array if it's not already
                    let audioData;
                    if (delta.audio instanceof Int16Array) {
                        audioData = delta.audio;
                    } else if (delta.audio instanceof ArrayBuffer) {
                        audioData = new Int16Array(delta.audio);
                    } else {
                        console.error('Unexpected audio data format:', typeof delta.audio);
                        return;
                    }

                    // Add the audio data to the stream player
                    wavStreamPlayerRef.current.add16BitPCM(audioData, item.id);
                    console.log('Added audio data to stream player');
                    
                    // Reset activity timer when assistant is streaming audio
                    resetActivityTimer();
                } catch (error) {
                    console.error('Error processing audio data:', error);
                }
            }

            // Update items state if needed
            if (item) {
                setItems(prevItems => {
                    const itemExists = prevItems.some(i => i.id === item.id);
                    if (itemExists) {
                        return prevItems.map(i => i.id === item.id ? {...i, ...item} : i);
                    }
                    return [...prevItems, item];
                });
            }
        } catch (error) {
            console.error('Error handling conversation update:', error);
        }
    }, [resetActivityTimer]);

    const handleMessage = useCallback((event: MessageEvent) => {
        try {
            const message = JSON.parse(event.data) as WebRTCMessage;
            console.log('🎯 Received WebRTC message:', {
                message
            });

            switch (message.type) {
                case "response.done":
                    const content = message.response?.output?.[0]?.content?.[0];
                    if (content?.transcript || content?.text) {
                        const transcript = content.transcript || content.text;
                        console.log('👩‍⚕️ Assistant:', transcript);
                        setItems(prevItems => [...prevItems, {
                            id: Date.now().toString(),
                            type: 'message',
                            role: 'assistant',
                            formatted: {transcript},
                            object: 'conversation.item'
                        }]);
                        // Log assistant message to Langfuse
                        logMessage({
                            role: 'assistant',
                            content: transcript,
                            messageId: Date.now().toString(),
                        });
                        // Reset activity timer when assistant responds (both text and voice)
                        resetActivityTimer();
                    }
                    break;
                case "conversation.item.input_audio_transcription.completed":
                    console.log("HII", message.transcript);
                    if (message.transcript && message.transcript.trim()) {
                        const transcript = message.transcript;
                        console.log('🗣️ User:', transcript);
                        setItems(prevItems => [...prevItems, {
                            id: Date.now().toString(),
                            type: 'message',
                            role: 'user',
                            formatted: {transcript},
                            object: 'conversation.item'
                        }]);
                        // Log user voice message to Langfuse
                        logMessage({
                            role: 'user',
                            content: transcript,
                            messageId: Date.now().toString(),
                        });
                        // Reset activity timer when user speaks (voice conversation)
                        resetActivityTimer();
                    }
                    break;

                case "conversation.item.created":
                    console.log("conversation.item.created", message.item?.content?.[0]?.text);
                    if (message.item?.content?.[0]?.text) {
                        const transcript = message.item.content[0].text;
                        console.log('🗣️ User:', transcript);
                        setItems(prevItems => [...prevItems, {
                            id: Date.now().toString(),
                            type: 'message',
                            role: 'user',
                            formatted: {transcript},
                            object: 'conversation.item'
                        }]);
                    }
                    break;
                case "response.created":
                    console.log("RESPONSE CREATED", message);
                    // Reset activity timer when assistant starts responding
                    resetActivityTimer();
                    break;

                default:
                    console.log('⚠️ Unhandled message type:', message.type);
            }
        } catch (error) {
            console.error('❌ Error processing message:', error);
        }
    }, [communicationMode, resetActivityTimer]);

    /**
     * Core WebRTC setup and event handlers
     */
    useEffect(() => {
        // Setup WebRTC event handlers
        const handleConnectionStateChange = () => {
            const state = peerConnectionRef.current?.connectionState;
            console.log('WebRTC connection state:', state);

            if (state === 'connected') {
                setIsConnected(true);
                // Send initial session configuration when connected
                sendMessage({
                    type: "session.update",
                    session: {
                        input_audio_transcription: {model: 'whisper-1', language: 'en'},
                        modalities: ["text", "audio"],  // Specify modalities here
                        voice: "alloy",         // Specify voice here
                        input_audio_format: "pcm16",
                        output_audio_format: "pcm16",
                        max_response_output_tokens: 1000,
                        turn_detection: {
                            type: "semantic_vad",
                            eagerness:"high",
                            prefix_padding_ms: 200,
                            silence_duration_ms: 500,
                            create_response: true
                        },
                        temperature: 0.8,
                        tools: [],
                        tool_choice: 'auto',
                    }
                });
            } else if (state === 'disconnected' || state === 'failed') {
                setIsConnected(false);
            }
        };


        const handleDataChannelMessage = (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data) as WebRTCMessage;
                console.log('Received message:', message);

                switch (message.type) {
                    case "realtime.event":
                        handleRealtimeEvent(message);
                        break;
                    case "error":
                        handleError(new Error(message.error || 'Unknown error'));
                        break;
                    case "conversation.interrupted":
                        handleInterruption();
                        break;
                    case "conversation.updated":
                        handleConversationUpdate(message);
                        break;
                    case "response.done":
                    case "conversation.item.input_audio_transcription.completed":
                    case "conversation.item.created":
                        // Handle message-related events
                        handleMessage(event);
                        break;
                    default:
                        console.log('Unhandled message type:', message.type);
                }
            } catch (error) {
                console.error('Error processing message:', error);
            }
        };

        // Set up event listeners
        if (peerConnectionRef.current) {
            peerConnectionRef.current.onconnectionstatechange = handleConnectionStateChange;
        }

        if (dataChannelRef.current) {
            dataChannelRef.current.addEventListener('message', handleDataChannelMessage);
        }

        // Cleanup function
        return () => {
            if (peerConnectionRef.current) {
                peerConnectionRef.current.onconnectionstatechange = null;
            }
            if (dataChannelRef.current) {
                dataChannelRef.current.removeEventListener('message', handleDataChannelMessage);
            }
        };
    }, [handleRealtimeEvent, handleError, handleInterruption, handleConversationUpdate, handleMessage]);

    /**
     * WebRTC setup functions
     */
    const setupWebRTC = async () => {
        try {
            // Get specialty from stored auth data
            const authData = storage.getAuthData();
            const specialty = authData?.session?.specialty;

            // Get ephemeral token from your backend with patient data
            const sessionPayload: { patient_name: string; patient_age: string; patient_gender: string; specialty?: string } = {
                patient_name: patientInfo.name,
                patient_age: calculateAge(patientInfo.dateOfBirth).toString(),
                patient_gender: patientInfo.gender
            };

            // Only include specialty if it exists in the magic link
            if (specialty) {
                sessionPayload.specialty = specialty;
            }

            const tokenResponse = await chatService.getChatbotSession(sessionPayload);

            if (!tokenResponse || tokenResponse.status !== 'success' || !tokenResponse.data?.client_secret?.value) {
                console.error('Invalid session response:', tokenResponse);
                throw new Error('Invalid session response from server');
            }

            // Use the client secret as the ephemeral key
            const EPHEMERAL_KEY = tokenResponse.data.client_secret.value;

            // Create and configure RTCPeerConnection
            const configuration = {
                iceServers: [
                    {urls: 'stun:stun.l.google.com:19302'}
                ]
            };
            peerConnectionRef.current = new RTCPeerConnection(configuration);

            // Setup audio only if in audio mode
            if (communicationMode === 'audio') {
                await setupAudio();
            }

            // Setup data channel
            setupDataChannel();

            // Ensure an audio transceiver is added so the SDP offer has an audio section.
            peerConnectionRef.current.addTransceiver('audio', {direction: 'recvonly'});

            const offer = await peerConnectionRef.current.createOffer();
            await peerConnectionRef.current.setLocalDescription(offer);

            // Send offer to OpenAI and get answer
            const baseUrl = "https://api.openai.com/v1/realtime";
            const model = 'gpt-realtime';

            const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
                method: "POST",
                body: offer.sdp,
                headers: {
                    Authorization: `Bearer ${EPHEMERAL_KEY}`,
                    "Content-Type": "application/sdp"
                },
            });

            if (!sdpResponse.ok) {
                const errorText = await sdpResponse.text();
                console.error('SDP response error:', errorText);
                throw new Error(`Failed to get SDP answer: ${sdpResponse.statusText}`);
            }

            const sdpAnswer = await sdpResponse.text();
            if (!sdpAnswer) {
                throw new Error('Empty SDP answer from server');
            }

            // Set remote description
            const answer = {
                type: "answer" as RTCSdpType,
                sdp: sdpAnswer,
            };
            await peerConnectionRef.current.setRemoteDescription(answer);

            // Wait for connection to establish
            await new Promise<void>((resolve, reject) => {
                if (!peerConnectionRef.current) {
                    reject(new Error('PeerConnection not initialized'));
                    return;
                }

                const checkState = () => {
                    const state = peerConnectionRef.current?.connectionState;
                    if (state === 'connected') {
                        resolve();
                    } else if (state === 'failed' || state === 'closed') {
                        reject(new Error(`WebRTC connection failed: ${state}`));
                    }
                };

                peerConnectionRef.current.onconnectionstatechange = checkState;
                // Also check immediately in case we're already connected
                checkState();

                // Set a timeout
                setTimeout(() => reject(new Error('WebRTC connection timeout')), 10000);
            });

            setIsConnected(true);
            console.log('WebRTC connection established successfully');
            
            // Start inactivity timer
            resetActivityTimer();

        } catch (error) {
            console.error('Error setting up WebRTC:', error);
            // Clean up any partial setup
            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }
            if (dataChannelRef.current) {
                dataChannelRef.current.close();
                dataChannelRef.current = null;
            }
            throw error;
        }
    };

    const setupAudio = async () => {
        if (!peerConnectionRef.current) {
            throw new Error('PeerConnection not initialized');
        }

        // Only proceed with audio setup if in audio mode
        if (communicationMode !== 'audio') {
            return;
        }

        try {
            // Create audio context with fallback
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Request microphone access with constraints
            const constraints = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 24000
                },
                video: false
            };

            // Setup microphone
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            const audioTrack = stream.getAudioTracks()[0];

            if (!audioTrack) {
                throw new Error('No audio track available');
            }

            // Configure audio track
            audioTrack.enabled = true;

            // Add to peer connection
            const sender = peerConnectionRef.current.addTrack(audioTrack, stream);

            // Store audio state
            audioStateRef.current = {
                stream,
                audioContext,
                audioTrack
            };

            // Handle incoming audio stream
            peerConnectionRef.current.ontrack = (event) => {
                // Only handle audio tracks if in audio mode
                if (communicationMode !== 'audio') {
                    return;
                }

                try {
                    const [remoteStream] = event.streams;
                    if (!remoteStream) {
                        console.warn('No remote stream available');
                        return;
                    }

                    // Create audio element only if it doesn't exist and in audio mode
                    if (!audioElementRef.current && communicationMode === 'audio') {
                        const audioEl = document.createElement("audio");
                        audioEl.autoplay = true;
                        audioElementRef.current = audioEl;
                    }

                    // Update the stream source only if audio element exists
                    if (audioElementRef.current) {
                        audioElementRef.current.srcObject = remoteStream;

                        // Ensure audio starts playing
                        const playPromise = audioElementRef.current.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(error => {
                                console.error('Audio playback error:', error);
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error handling remote audio stream:', error);
                }
            };

            console.log('Audio setup completed successfully');
        } catch (error) {
            console.error('Error setting up audio:', error);
            // Clean up any partial setup
            if (audioStateRef.current.stream) {
                audioStateRef.current.stream.getTracks().forEach(track => track.stop());
            }
            if (audioStateRef.current.audioContext) {
                await audioStateRef.current.audioContext.close();
            }
            audioStateRef.current = {
                stream: null,
                audioContext: null,
                audioTrack: null
            };
            throw error;
        }
    };

    const setupDataChannel = () => {
        if (!peerConnectionRef.current) return;

        dataChannelRef.current = peerConnectionRef.current.createDataChannel("oai-events");
        dataChannelRef.current.onopen = onDataChannelOpen;
        // Message listener is added in the useEffect hook to avoid duplicates
    };

    const onDataChannelOpen = () => {
        // Send initial session configuration with healthcare assistant instructions
        sendMessage({
            type: "session.update",
            session: {
                input_audio_transcription: communicationMode === 'audio' ? {model: 'gpt-4o-transcribe', language: 'en'} : undefined,
                modalities: communicationMode === 'audio' ? ["text", "audio"] : ["text"],
                voice: communicationMode === 'audio' ? "alloy" : undefined,
                input_audio_format: communicationMode === 'audio' ? "pcm16" : undefined,
                output_audio_format: communicationMode === 'audio' ? "pcm16" : undefined,
                max_response_output_tokens: 1000,
                turn_detection: communicationMode === 'audio' ? {
                        type: "semantic_vad",
                        eagerness:"medium",
                        create_response: true,
                        interrupt_response: true
                } : undefined,
                temperature: 0.8,
                tools: [],
                tool_choice: 'auto',
            }
        });

        // Send initial greeting
        sendMessage({
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: "Hello"
                    }
                ]
            }
        });

        sendMessage({
            "type": "response.create",
            "response": {
                "modalities": communicationMode === 'audio' ? ["text", "audio"] : ["text"],
                "voice": communicationMode === 'audio' ? "alloy" : undefined,
                "output_audio_format": communicationMode === 'audio' ? "pcm16" : undefined,
                "temperature": 0.8,
                "max_output_tokens": 1000
            }
        });
    };

    const sendMessage = (message: WebRTCMessage) => {
        if (dataChannelRef.current?.readyState === "open") {
            dataChannelRef.current.send(JSON.stringify(message));
            console.log('Sent message:', message);
        }
    };

    // Update the conversation item rendering
    const renderConversationItem = (item: ConversationItem) => (
        <div
            className="mb-6 last:mb-0"
            key={item.id}
        >
            <div className="flex items-center justify-between mb-1 px-1">
                <div className="text-sm font-medium text-[#6c757d] capitalize">
                    {(item.role || item.type).replaceAll('_', ' ')}
                </div>
            </div>
            <div
                className={`rounded-2xl p-4 border ${item.role === 'user'
                    ? 'bg-[rgba(0,166,147,0.05)] border-[rgba(0,166,147,0.1)] mr-8 rounded-br-sm'
                    : 'bg-[rgba(26,59,93,0.05)] border-[rgba(26,59,93,0.1)] ml-8 rounded-bl-sm'
                }`}
            >
                {item.type === 'function_call_output' && (
                    <div className="whitespace-pre-wrap text-[#2c3e50] text-[0.9375rem] leading-relaxed">
                        {item.formatted.output}
                    </div>
                )}
                {!!item.formatted.tool && (
                    <div
                        className="font-mono text-sm bg-[rgba(0,0,0,0.03)] p-3 rounded-lg my-3 border border-[rgba(0,0,0,0.05)]">
                        {item.formatted.tool.name}(
                        {item.formatted.tool.arguments})
                    </div>
                )}
                {!item.formatted.tool && item.role === 'user' && (
                    <div className="whitespace-pre-wrap text-[#2c3e50] text-[0.9375rem] leading-relaxed">
                        {item.formatted.transcript ||
                            (item.formatted.audio?.byteLength
                                ? '(awaiting transcript)'
                                : item.formatted.text ||
                                '(item sent)')}
                    </div>
                )}
                {!item.formatted.tool && item.role === 'assistant' && (
                    <div className="whitespace-pre-wrap text-[#2c3e50] text-[0.9375rem] leading-relaxed">
                        {item.formatted.transcript ||
                            item.formatted.text ||
                            '(truncated)'}
                    </div>
                )}
            </div>
        </div>
    );

    const validateForm = () => {
        let isValid = true;
        const errors = {
            name: '',
            dateOfBirth: '',
            gender: '',
            communicationMode: ''
        };

        if (!patientInfo.name.trim()) {
            errors.name = 'Preferred Name is required';
            isValid = false;
        }

        if (!patientInfo.dateOfBirth) {
            errors.dateOfBirth = 'Date of Birth is required';
            isValid = false;
        } else {
            const age = calculateAge(patientInfo.dateOfBirth);
            if (age < 0 || age > 120) {
                errors.dateOfBirth = 'Please enter a valid date of birth';
                isValid = false;
            }
        }

        if (!patientInfo.gender) {
            errors.gender = 'Gender at Birth is required';
            isValid = false;
        }

        if (!communicationMode) {
            errors.communicationMode = 'My preferred mode of communication is required';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    // Add the sendTextMessage function
    const sendTextMessage = useCallback(() => {
        if (!textMessage.trim()) return;

        // Reset activity timer on user interaction
        resetActivityTimer();

        // Log user text message to Langfuse
        logMessage({
            role: 'user',
            content: textMessage.trim(),
            messageId: Date.now().toString(),
        });

        sendMessage({
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: textMessage.trim()
                    }
                ]
            }
        });

        sendMessage({
            "type": "response.create",
            "response": {
                "modalities": communicationMode === 'audio' ? ["text", "audio"] : ["text"],
                "voice": communicationMode === 'audio' ? "alloy" : undefined,
                "output_audio_format": communicationMode === 'audio' ? "pcm16" : undefined,
                "temperature": 0.8,
                "max_output_tokens": 1000
            }
        });

        setTextMessage('');
    }, [textMessage, communicationMode, resetActivityTimer]);

    /**
     * Render the application
     */
    return (
        <div data-component="PatientHistoryPage"
             className="patient-history-page w-full h-screen flex flex-col bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef] overflow-hidden">
            <ParticleBackground/>

            {/* Fixed navigation bar for mobile */}
            <header
                className="sticky top-0 z-20 w-full bg-white/90 backdrop-blur-md shadow-sm border-b border-[#e2e8f0]">
                <div className="max-w-7xl mx-auto px-4">
                    {/* Navigation bar with proper layout */}
                    <div className="flex items-center justify-between h-16 md:h-20">
                        {/* Left side: AIGP Health logo and Doc Assist */}
                        <div className="flex items-center">
                            <img
                                src="/aigp-logo.png"
                                alt="AIGP Health Logo"
                                className="w-12 h-12 md:w-16 md:h-16 object-contain"
                            />
                            <div className="ml-2 md:ml-3">
                                <div
                                    className="font-bold text-base md:text-lg bg-gradient-to-r from-[#1E355C] to-[#356BAD] bg-clip-text text-transparent">
                                    Doc Assist
                                </div>
                            </div>
                        </div>

                        {/* Removed hardcoded clinic-specific content for white-labeling */}
                        {/* Center divider - only visible on md and larger screens */}
                        {/* <div className="hidden md:block h-8 w-px bg-gray-300 mx-4"></div> */}

                        {/* Right side: MediPoint Medical Centre with clinic logo */}
                        {/* <div className="hidden md:flex items-center">
                            <img
                                src="/clinic-logo.png"
                                alt="MediPoint Logo"
                                className="w-8 h-8 object-contain"
                            />
                            <div className="ml-2">
                                <div className="text-[#1E355C] font-medium text-sm">The Doctors Werribee</div>

                            </div>
                        </div> */}

                        {/* Mobile version of clinic info - only visible on small screens */}
                        {/* <div className="flex md:hidden items-center">
                            <img
                                src="/clinic-logo.png"
                                alt="MediPoint Logo"
                                className="w-8 h-8 object-contain"
                            />
                            <div className="ml-2">
                                <div className="text-[#1E355C] font-medium text-xs">MediPoint Medical</div>
                                <div className="text-[#6c757d] text-xs">WOODLANDS BRANCH</div>
                            </div>
                        </div> */}

                        {/* Logout button - text only on larger screens */}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 text-[#6c757d] hover:text-[#dc3545] hover:bg-transparent"
                            onClick={async () => {
                                if (isConnected) {
                                    await disconnectConversation(false);
                                }
                                onLogout();
                            }}
                        >
                            <span className="hidden md:inline mr-1">Logout</span>
                            <X size={16} className="md:size-5"/>
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main content area with flex-1 to take available space */}
            <main className="flex-1 w-full max-w-4xl mx-auto p-4 md:p-6 overflow-hidden flex flex-col">
                <Card
                    className="w-full h-full flex flex-col overflow-hidden border-[#e2e8f0] bg-white/90 backdrop-blur-sm">
                    <CardHeader
                        className="p-4 md:p-6 border-b border-[#e2e8f0] flex flex-row items-center justify-between shrink-0">
                        <CardTitle className="text-lg md:text-xl font-semibold text-[#2c3e50]">Patient
                            Information</CardTitle>
                        {isConnected && (
                            <div className="flex items-center gap-2 text-sm md:text-base text-[#356BAD]">
                                <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#356BAD]"></span>
                                <span>Session Active</span>
                            </div>
                        )}
                    </CardHeader>

                    <CardContent className="p-0 flex-1 flex flex-col overflow-hidden">
                        {showThankYou ? (
                            <div className="flex flex-col items-center justify-center p-8 md:p-10 text-center h-full">
                                <Zap size={32} className="text-[#356BAD] mb-4 md:size-40 md:mb-6"/>
                                <p className="text-lg md:text-xl font-medium text-[#2c3e50] mb-2">Thank you for using
                                    AIGP Health Doc Assist</p>
                                <span className="text-sm md:text-base text-[#6c757d]">Logging out...</span>
                            </div>
                        ) : items.length === 0 ? (
                            isConnecting ? (
                                <div
                                    className="flex flex-col items-center justify-center p-8 md:p-10 text-center h-full">
                                    <Zap size={32} className="text-[#356BAD] mb-4 animate-pulse md:size-40 md:mb-6"/>
                                    <p className="text-lg md:text-xl font-medium text-[#2c3e50]">Your conversation will
                                        appear here</p>
                                </div>
                            ) : (
                                // Patient form with ShadCN components - increased size for iPad
                                <div className="p-4 md:p-6 h-full overflow-y-auto">
                                    <div className="space-y-5 md:space-y-6 max-w-md md:max-w-lg mx-auto">
                                        {/* Name field */}
                                        <div>
                                            <Label htmlFor="name"
                                                   className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">Preferred Name</Label>
                                            <Input
                                                id="name"
                                                value={patientInfo.name}
                                                onChange={(e) => {
                                                    setPatientInfo(prev => ({...prev, name: e.target.value}));
                                                    setFormErrors(prev => ({...prev, name: ''}));
                                                }}
                                                placeholder="Enter patient's full name"
                                                className={`w-full rounded-lg border-2 ${formErrors.name ? 'border-[#dc3545]' : 'border-[#e2e8f0]'
                                                } focus-visible:border-[#356BAD] focus-visible:ring-[#356BAD]/10 h-11 md:h-14 md:text-lg md:px-4`}
                                            />
                                            {formErrors.name && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.name}</span>}
                                        </div>

                                        {/* Date of Birth field - now in its own row */}
                                        <div className="relative">
                                            <Label htmlFor="dateOfBirth"
                                                   className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">Date of Birth</Label>
                                            <div className="relative w-full">
                                                <DatePicker
                                                    ref={datePickerRef}
                                                    id="dateOfBirth"
                                                    selected={patientInfo.dateOfBirth ? new Date(patientInfo.dateOfBirth) : null}
                                                    onChange={(date: Date | null) => {
                                                        if (date) {
                                                            const formattedDate = date.toISOString().split('T')[0];
                                                            setPatientInfo(prev => ({...prev, dateOfBirth: formattedDate}));
                                                            setFormErrors(prev => ({...prev, dateOfBirth: ''}));
                                                        }
                                                        setIsDatePickerOpen(false);
                                                    }}
                                                    open={isDatePickerOpen}
                                                    onCalendarOpen={() => setIsDatePickerOpen(true)}
                                                    onCalendarClose={() => setIsDatePickerOpen(false)}
                                                    onInputClick={() => {
                                                        setIsDatePickerOpen(!isDatePickerOpen);
                                                    }}
                                                    onClickOutside={() => setIsDatePickerOpen(false)}
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="Select date of birth"
                                                    showYearDropdown
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={100}
                                                    dropdownMode="select"
                                                    maxDate={new Date()}
                                                    minDate={new Date(1900, 0, 1)}
                                                    showMonthDropdown
                                                    popperPlacement="top-start"
                                                    preventOpenOnFocus={true}
                                                    autoFocus={false}
                                                    portalId="root"
                                                    shouldCloseOnSelect={true}
                                                    className={`w-full rounded-lg border-2 px-3 md:px-4 h-11 md:h-14 md:text-lg pr-10 ${formErrors.dateOfBirth ? 'border-[#dc3545]' : 'border-[#e2e8f0]'
                                                    } focus:outline-none focus:border-[#356BAD] focus:ring-2 focus:ring-[#356BAD]/10 bg-white cursor-pointer`}
                                                    wrapperClassName="w-full"
                                                    calendarClassName="date-picker-calendar"
                                                />
                                                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 md:h-6 md:w-6 text-[#6c757d] pointer-events-none z-10" />
                                            </div>
                                            {formErrors.dateOfBirth && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.dateOfBirth}</span>}
                                        </div>

                                        {/* Gender field - improved styling to match other components */}
                                        <div>
                                            <Label htmlFor="gender"
                                                   className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">Gender at Birth</Label>
                                            <div className={`relative ${formErrors.gender ? 'mb-1' : ''}`}>
                                                <select
                                                    id="gender"
                                                    value={patientInfo.gender}
                                                    onChange={(e) => {
                                                        setPatientInfo(prev => ({...prev, gender: e.target.value}));
                                                        setFormErrors(prev => ({...prev, gender: ''}));
                                                    }}
                                                    className={`w-full h-11 md:h-14 px-3 md:px-4 rounded-lg border-2 appearance-none ${formErrors.gender ? 'border-[#dc3545]' : 'border-[#e2e8f0]'
                                                    } focus:outline-none focus:border-[#356BAD] focus:ring-2 focus:ring-[#356BAD]/10 bg-white text-[#2c3e50] md:text-lg`}
                                                >
                                                    <option value="" className="text-[#6c757d]">Select gender at birth</option>
                                                    <option value="Male" className="text-[#2c3e50]">Male</option>
                                                    <option value="Female" className="text-[#2c3e50]">Female</option>
                                                </select>
                                                {/* Custom dropdown arrow that matches the theme */}
                                                <div
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3 md:pr-4 pointer-events-none">
                                                    <ChevronDown className="h-5 w-5 md:h-6 md:w-6 text-[#6c757d]"/>
                                                </div>
                                            </div>
                                            {formErrors.gender && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.gender}</span>}
                                        </div>

                                        {/* Communication mode with icons added */}
                                        <div>
                                            <Label
                                                className="text-[#2c3e50] font-medium mb-1.5 md:mb-2 block md:text-lg">My preferred mode of communication is</Label>
                                            <div className="grid grid-cols-2 gap-3 md:gap-4">
                                                <Button
                                                    type="button"
                                                    variant={communicationMode === 'audio' ? 'default' : 'outline'}
                                                    className={`h-11 md:h-14 rounded-lg font-medium md:text-lg ${communicationMode === 'audio'
                                                        ? 'bg-[#356BAD] text-white border-[#356BAD] shadow-sm'
                                                        : 'border-[#e2e8f0] text-[#2c3e50] hover:border-[#356BAD] hover:text-[#356BAD]'
                                                    }`}
                                                    onClick={() => {
                                                        setCommunicationMode('audio');
                                                        setFormErrors(prev => ({...prev, communicationMode: ''}));
                                                    }}
                                                >
                                                    <Mic className="mr-2 md:mr-3 md:size-5" size={16}/>
                                                    Voice
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant={communicationMode === 'text' ? 'default' : 'outline'}
                                                    className={`h-11 md:h-14 rounded-lg font-medium md:text-lg ${communicationMode === 'text'
                                                        ? 'bg-[#356BAD] text-white border-[#356BAD] shadow-sm'
                                                        : 'border-[#e2e8f0] text-[#2c3e50] hover:border-[#356BAD] hover:text-[#356BAD]'
                                                    }`}
                                                    onClick={() => {
                                                        setCommunicationMode('text');
                                                        setFormErrors(prev => ({...prev, communicationMode: ''}));
                                                    }}
                                                >
                                                    <MessageSquare className="mr-2 md:mr-3 md:size-5" size={16}/>
                                                    Text
                                                </Button>
                                            </div>
                                            {formErrors.communicationMode && <span
                                                className="text-[#dc3545] text-sm md:text-base mt-1 block">{formErrors.communicationMode}</span>}
                                        </div>

                                        {/* Start New Session button - positioned right after communication mode */}
                                        <div className="pt-2">
                                            <Button
                                                className="w-full h-11 md:h-14 bg-[#356BAD] hover:bg-[#356BAD]/90 text-white font-medium rounded-lg shadow-sm md:text-lg"
                                                onClick={connectConversation}
                                                style={{visibility: isConnecting ? 'hidden' : 'visible'}}
                                            >
                                                <Zap className="mr-2 md:mr-3 md:size-5" size={16}/>
                                                Start New Session
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )
                        ) : (
                            <div className="flex flex-col h-full">
                                {/* Scrollable conversation area with scroll-behavior: smooth for better UX */}
                                <div
                                    id="conversation-container"
                                    className="flex-1 p-4 md:p-6 overflow-y-auto scroll-smooth"
                                    style={{
                                        scrollBehavior: 'smooth',
                                    }}
                                >
                                    <div
                                        className="space-y-4 md:space-y-6"
                                        key={`conversation-${items.length}`}
                                    >
                                        {items.map(renderConversationItem)}
                                        {/* Invisible element at the bottom that we'll scroll to */}
                                        <div
                                            id="conversation-end"
                                            className="h-1"
                                            ref={(el) => {
                                                if (el && items.length > 0) {
                                                    el.scrollIntoView({behavior: 'smooth'});
                                                }
                                            }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Fixed text input at bottom */}
                                {isConnected && communicationMode === 'text' && (
                                    <div className="border-t border-[#e2e8f0] p-4 md:p-5 bg-white">
                                        <div className="flex items-center gap-2 md:gap-3">
                                            <Input
                                                type="text"
                                                value={textMessage}
                                                onChange={(e) => setTextMessage(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        sendTextMessage();
                                                    }
                                                }}
                                                placeholder="Type your message..."
                                                className="flex-1 h-11 md:h-14 rounded-lg border-2 border-[#e2e8f0] focus-visible:border-[#356BAD] focus-visible:ring-[#356BAD]/10 md:text-lg md:px-4"
                                            />
                                            <Button
                                                onClick={sendTextMessage}
                                                disabled={!textMessage.trim()}
                                                className="h-11 md:h-14 px-4 md:px-6 bg-[#356BAD] hover:bg-[#356BAD]/90 text-white md:text-lg"
                                            >
                                                Send
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>

                    {/* Only show footer when session is connected */}
                    {(isConnected || showThankYou) && (
                        <CardFooter className="p-4 md:p-6 border-t border-[#e2e8f0] shrink-0">
                            <div className="w-full flex justify-center">
                                <Button
                                    variant="outline"
                                    className={`h-11 px-6 border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#dc3545] ${isEndingSession ? 'opacity-70 pointer-events-none' : ''}`}
                                    onClick={() => setShowEndSessionDialog(true)}
                                    disabled={isEndingSession || showThankYou}
                                >
                                    {isEndingSession ? 'Saving Session...' : 'End Session'}
                                    {!isEndingSession && <X className="ml-2" size={16}/>}
                                </Button>
                            </div>
                        </CardFooter>
                    )}
                </Card>
            </main>

            {/* End Session Confirmation Dialog */}
            <Dialog open={showEndSessionDialog} onOpenChange={setShowEndSessionDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold text-[#2c3e50]">End Session</DialogTitle>
                        <DialogDescription className="text-[#6c757d]">
                            Are you sure you want to end this session? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-3 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => setShowEndSessionDialog(false)}
                            className="flex-1 sm:flex-none border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#2c3e50]"
                        >
                            Continue Session
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowEndSessionDialog(false);
                                disconnectConversation();
                            }}
                            className="flex-1 sm:flex-none bg-[#dc3545] hover:bg-[#dc3545]/90"
                        >
                            End Session
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* AI Disclaimer Dialog */}
            <Dialog open={showDisclaimerDialog} onOpenChange={setShowDisclaimerDialog}>
                <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-[#2c3e50] mb-4">AIGP Health Platform General Disclaimer</DialogTitle>
                        <DialogDescription className="text-[#2c3e50] space-y-4 text-sm leading-relaxed">
                            <p className="font-semibold text-[#dc3545] text-base">
                                IMPORTANT: PLEASE READ THIS DISCLAIMER CAREFULLY BEFORE USING THE AIGP HEALTH PLATFORM.
                            </p>
                            
                            <p className="font-semibold">By using the AIGP Health AI platform "Anzu," users acknowledge and agree to the following:</p>

                            <ul className="list-disc pl-5 space-y-3">
                                <li>This platform uses Artificial intelligence (AI) to gather your information.</li>
                                <li>Information entered into the AI system will be forwarded to your treating clinician at the time of your scheduled consultation.</li>
                                <li>It will not be accessed, reviewed, or acted upon by any doctor prior to your appointment.</li>
                                <li>This platform is not intended for use in medical emergencies.</li>
                                <li>If you are experiencing chest pain, difficulty breathing, severe bleeding, or any other serious or life-threatening condition hang up now and dial 000 immediately.</li>
                                <li>If your condition worsens after completing this pre-consult session, you agree to seek emergency medical care or contact your local hospital.</li>
                                <li>By continuing to use this platform, you confirm that this is not a medical emergency and that you understand Anzu is a pre-consult tool only.</li>
                                <li>AIGP Health accepts no responsibility for outcomes arising from delays in care where emergency attention was required.</li>
                            </ul>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-3 sm:gap-0 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setShowDisclaimerDialog(false)}
                            className="flex-1 sm:flex-none border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#2c3e50]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={actuallyConnectConversation}
                            className="flex-1 sm:flex-none bg-[#356BAD] hover:bg-[#356BAD]/90 text-white"
                        >
                            I Agree - Start Session
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Session Timeout Warning Dialog */}
            <Dialog open={showTimeoutWarning} onOpenChange={(open) => {
                if (!open) {
                    // User clicked continue - reset the timer
                    resetActivityTimer();
                }
                setShowTimeoutWarning(open);
            }}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold text-[#2c3e50]">Session Timeout Warning</DialogTitle>
                        <DialogDescription className="text-[#6c757d] space-y-3">
                            <p>Your session has been inactive for 60 seconds.</p>
                            <p className="font-semibold text-[#dc3545]">
                                Your session will automatically end in {timeoutCountdown} seconds.
                            </p>
                            <p>Click "Continue Session" to keep working, or allow the session to end automatically.</p>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-3 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowTimeoutWarning(false);
                                resetActivityTimer();
                            }}
                            className="flex-1 sm:flex-none border-[#e2e8f0] text-[#6c757d] hover:bg-[#f8f9fa] hover:text-[#2c3e50]"
                        >
                            Continue Session
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowTimeoutWarning(false);
                                disconnectConversation(true);
                            }}
                            className="flex-1 sm:flex-none bg-[#dc3545] hover:bg-[#dc3545]/90"
                        >
                            End Now
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

